/**
 * Offer letter formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_office_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const offerLetterFormatsPack: ToilePack = {
    slug: 'offer-letter-formats',
    category: 'office',
    name: 'Offer letter formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'job', label: 'Job offer', subject: 'an offer letter format on letterhead with position, salary and joining date placeholders'},
        {key: 'intern', label: 'Internship', subject: 'an internship offer letter format'},
        {key: 'promotion', label: 'Promotion', subject: 'a promotion letter format'},
    ],
    styles: [
        {key: 'plain', label: 'Plain', style: 'plain formal letter typesetting in a black serif with a ruled signature block', palette: 'black ink on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'corporate', label: 'Corporate', style: 'corporate letterhead with a coloured top band, an empty logo box at left and a thin footer line', palette: 'royal blue and grey on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'elegant', label: 'Elegant', style: 'elegant formal letter on cream paper inside a thin gold double border', palette: 'gold and dark brown on cream', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
    ],
    directions: ['green header', 'gold border', 'with seal'],
    directionHint: 'try: green header',
    seo: {
        title: 'Offer letter format: job, internship, promotion',
        metaDescription: 'Nine printable offer letter formats for a job, an internship or a promotion, each in a plain, corporate or elegant style.',
        h1: 'Offer letter format: job, internship, promotion',
        tagline: 'Three offer letters, each in three styles, with placeholders for the position, salary and joining date.',
        intro: 'An offer letter format is the layout an employer uses to make a job offer. These nine show a job offer on letterhead with placeholders for position, salary and joining date; an internship offer; and a promotion letter. Only the headings are lettered; grey lines hold the terms. Type a twist, such as green header, and it is a request.',
        useCases: [
            'a job offer to a new hire',
            'an internship offer for a student or trainee',
            'a promotion letter for a staff member',
            'a template for an office to fill in each time',
        ],
        faq: [
            {q: 'What placeholders does the job offer have?', a: 'Position, salary and joining date are shown as blank lines, so you write in your own terms.'},
            {q: 'Is the salary filled in?', a: 'No. The salary is a placeholder line. You write the figure yourself.'},
            {q: 'How do the three letters differ?', a: 'The job offer sets a position and salary, the internship offer is for a student or trainee, and the promotion letter is for someone already employed.'},
            {q: 'Can I add a seal or a green header?', a: 'Type it as a twist, such as with seal or green header. It is a request for that look, not a promised result.'},
        ],
    },
}
